import { useCallback, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

type SuggestionSource = 'cache' | 'mapbox';

export interface AddressSuggestion {
  label: string;
  source: SuggestionSource;
  coordinates?: [number, number];
}

const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || process.env.MAPBOX_ACCESS_TOKEN;

const sanitizeQueryForIlike = (query: string) =>
  query.replace(/[%_]/g, '\\$&').replace(/'/g, "''");

export const useAddressAutocomplete = () => {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef<Map<string, AddressSuggestion[]>>(new Map());
  const controllerRef = useRef<AbortController | null>(null);

  const fetchCachedSuggestions = useCallback(
    async (query: string): Promise<AddressSuggestion[]> => {
      const escaped = sanitizeQueryForIlike(query);
      const { data, error } = await supabase
        .from('address_cache')
        .select('address, coordinates, updated_at')
        .ilike('address', `%${escaped}%`)
        .order('updated_at', { ascending: false })
        .limit(8);

      if (error) {
        console.error('Supabase address cache error:', error);
        return [];
      }

      return (
        data?.map((row) => ({
          label: row.address,
          source: 'cache' as SuggestionSource,
          coordinates: Array.isArray(row.coordinates)
            ? (row.coordinates as [number, number])
            : undefined,
        })) ?? []
      );
    },
    []
  );

  const fetchMapboxSuggestions = useCallback(
    async (
      query: string,
      existingLabels: Set<string>
    ): Promise<AddressSuggestion[]> => {
      if (!MAPBOX_TOKEN) {
        console.warn('Missing Mapbox token: REACT_APP_MAPBOX_ACCESS_TOKEN');
        return [];
      }

      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&types=address,poi&country=US`,
        { signal: controller.signal }
      );

      if (!response.ok) {
        if (response.status !== 499) {
          console.error('Mapbox autocomplete error:', await response.text());
        }
        return [];
      }

      const payload = await response.json();
      const features: { place_name: string; center?: [number, number] }[] =
        payload?.features ?? [];

      return features
        .filter(
          (feature) =>
            !!feature.place_name && !existingLabels.has(feature.place_name)
        )
        .map((feature) => ({
          label: feature.place_name,
          source: 'mapbox' as SuggestionSource,
          coordinates: feature.center as [number, number] | undefined,
        }));
    },
    []
  );

  const fetchSuggestions = useCallback(
    async (rawQuery: string) => {
      const query = rawQuery.trim();

      if (query.length < 3) {
        controllerRef.current?.abort();
        setSuggestions([]);
        setLoading(false);
        return;
      }

      const cacheKey = query.toLowerCase();
      if (cacheRef.current.has(cacheKey)) {
        setSuggestions(cacheRef.current.get(cacheKey) ?? []);
        return;
      }

      setLoading(true);
      try {
        const cachedSuggestions = await fetchCachedSuggestions(query);
        const existing = new Set(cachedSuggestions.map((item) => item.label));

        let combined = cachedSuggestions;
        if (cachedSuggestions.length < 5) {
          const mapboxSuggestions = await fetchMapboxSuggestions(query, existing);
          combined = [...cachedSuggestions, ...mapboxSuggestions].slice(0, 8);
        }

        cacheRef.current.set(cacheKey, combined);
        setSuggestions(combined);
      } catch (error) {
        if ((error as any)?.name !== 'AbortError') {
          console.error('Autocomplete suggestions error:', error);
        }
      } finally {
        setLoading(false);
      }
    },
    [fetchCachedSuggestions, fetchMapboxSuggestions]
  );

  const clearSuggestions = useCallback(() => {
    controllerRef.current?.abort();
    setSuggestions([]);
    setLoading(false);
  }, []);

  return {
    suggestions,
    loading,
    fetchSuggestions,
    clearSuggestions,
  };
};

